import React, { useState } from 'react';
import { Alert, Anchor, Button, Divider, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExclamationCircle } from '@tabler/icons-react';
import AuthLayout from './AuthLayout';
import { appConfig } from '../../data/AppConfig';
import { apiErrorMessage } from '../../data/apiClient';
import { loginWithPassword } from '../../data/PasswordAuth';
import { useCsrf } from '../../hooks/useCsrf';
import googlelogo from '../../assets/google.png';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { consumeReturnPath, resolveLoginIntended } from '../../utils/authRedirect';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';

const LogInForm = () => {
    const { csrfToken, refreshCsrfToken } = useCsrf();
    const [isLoggingInGoogle, setIsLoggingInGoogle] = useState(false);
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [formError, setFormError] = useState('');
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    /**
     * The SSO callback redirects here with ?error= when it refuses a login —
     * unknown account, or an account with no role assignment. Previously nothing
     * read it, so those users saw an unexplained sign-in page.
     */
    const ssoError = searchParams.get('error');

    const form = useForm({
        initialValues: { email: '', password: '' },
        validate: {
            email: value => (/^\S+@\S+\.\S+$/.test(value.trim()) ? null : 'Enter a valid email'),
            password: value => (value.length > 0 ? null : 'Enter your password'),
        },
    });

    const handleLoginWithGoogle = () => {
        setIsLoggingInGoogle(true);
        // Success lands back via SSO redirect, so the click is the last event
        // we can emit from this page for the Google path.
        track(AnalyticsEvents.SignInSubmitted, { method: 'google' });
        const intended = resolveLoginIntended(location.state?.from);
        consumeReturnPath();
        refreshCsrfToken().request.then(() => {
            window.location.assign(
                `${appConfig.REACT_APP_BACKEND_URL}/api/auth/redirect/google?intended=${encodeURIComponent(intended)}`,
            );
        });
    };

    const handlePasswordSignIn = async (values: typeof form.values) => {
        setIsSigningIn(true);
        setFormError('');
        track(AnalyticsEvents.SignInSubmitted, { method: 'password' });
        try {
            await loginWithPassword(csrfToken, {
                email: values.email.trim(),
                password: values.password,
            });

            track(AnalyticsEvents.SignInSucceeded, { method: 'password' });
            // Full reload rather than a router navigate: the session cookie just
            // changed, and useAuth's SWR cache needs to be rebuilt from scratch.
            const intended = resolveLoginIntended(location.state?.from);
            consumeReturnPath();
            window.location.assign(intended);
        } catch (err: any) {
            track(AnalyticsEvents.SignInFailed, { method: 'password' });
            setFormError(apiErrorMessage(err, 'Could not sign you in.'));
            setIsSigningIn(false);
        }
    };

    return (
        <AuthLayout>
            <Stack gap="lg">
                <Text
                    style={{
                        fontSize: 'clamp(2rem, 5vw, 3rem)',
                        lineHeight: 1.05,
                        fontWeight: 700,
                        color: 'var(--foreground)',
                        letterSpacing: '-0.02em',
                    }}
                >
                    Hi there!
                </Text>
                <div
                    style={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        lineHeight: 1.5,
                        color: 'var(--foreground-secondary)',
                    }}
                >
                    Welcome to{' '}
                    <span className="watchly-gradient-text" style={{ fontWeight: 700 }}>
                        Watchly
                    </span>
                    .
                </div>

                {(ssoError || formError) && (
                    <Alert
                        variant="light"
                        color="red"
                        icon={<IconExclamationCircle size={18} />}
                        title="Couldn’t sign you in"
                    >
                        {ssoError ?? formError}
                    </Alert>
                )}

                <form onSubmit={form.onSubmit(handlePasswordSignIn)}>
                    <Stack gap="sm">
                        <TextInput
                            label="Email"
                            placeholder="you@company.com"
                            autoComplete="email"
                            disabled={isSigningIn}
                            {...form.getInputProps('email')}
                        />
                        <PasswordInput
                            label="Password"
                            autoComplete="current-password"
                            disabled={isSigningIn}
                            {...form.getInputProps('password')}
                        />
                        <Button type="submit" size="md" radius={9999} loading={isSigningIn}>
                            Sign in
                        </Button>
                        <Anchor
                            onClick={() => navigate('/recover-password')}
                            style={{ fontSize: '.875rem', fontWeight: 600, alignSelf: 'flex-start' }}
                        >
                            Forgot password?
                        </Anchor>
                    </Stack>
                </form>

                <Divider label="or" labelPosition="center" />

                <Button
                    variant="default"
                    size="lg"
                    radius={9999}
                    loading={isLoggingInGoogle}
                    onClick={handleLoginWithGoogle}
                    leftSection={<img src={googlelogo} alt="" width={22} height={22} style={{ display: 'block' }} />}
                >
                    Continue with Google
                </Button>

                <Text style={{ fontSize: '.875rem', fontWeight: 400, color: 'var(--foreground-muted)' }}>
                    Watchly is invite only. Ask your account manager for an invitation.
                </Text>
            </Stack>
        </AuthLayout>
    );
};

export default LogInForm;
